import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../ui/Icon';

export interface View2D {
  x: number;
  y: number;
  k: number;
}

/** Converte um ponto do mapa (unidades do viewBox) para pixels da tela do viewport. */
export type Project = (x: number, y: number) => { left: number; top: number };

const DRAG_THRESHOLD = 5;
const MAX_ZOOM = 5;

function isTyping(el: EventTarget | null): boolean {
  const t = el as HTMLElement | null;
  if (!t) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable;
}

/**
 * Mapa em tela cheia com zoom e arrasto.
 * - Arrastar (botão esquerdo, com folga de 5 px para não roubar cliques), espaço + arrastar ou botão do meio.
 * - Roda do mouse ou pinça no touch: zoom em volta do ponteiro.
 * - Teclas + e − dão zoom; 0 enquadra o mapa.
 * `overlay` desenha elementos de tela presos a pontos do mapa (ex.: card do boss), sem escalar junto.
 */
export function MapViewport({
  width,
  height,
  children,
  overlay,
}: {
  width: number;
  height: number;
  children: ReactNode;
  overlay?: (project: Project, view: View2D) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View2D>({ x: 0, y: 0, k: 1 });
  const [space, setSpace] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fitK = useRef(1);
  const userMoved = useRef(false);
  const gesture = useRef<{
    pointers: Map<number, { x: number; y: number }>;
    start?: { x: number; y: number; vx: number; vy: number; id: number };
    moved: boolean;
    pinch?: { dist: number; mx: number; my: number; view: View2D };
    suppressClick: boolean;
  }>({ pointers: new Map(), moved: false, suppressClick: false });

  const size = () => {
    const r = ref.current?.getBoundingClientRect();
    return { W: r?.width ?? 1, H: r?.height ?? 1, left: r?.left ?? 0, top: r?.top ?? 0 };
  };

  /** Mantém pelo menos o centro da tela sobre o mapa. */
  const clamp = useCallback(
    (v: View2D): View2D => {
      const { W, H } = size();
      const k = Math.min(Math.max(v.k, fitK.current * 0.85), fitK.current * MAX_ZOOM);
      const x = Math.min(Math.max(v.x, W / 2 - width * k), W / 2);
      const y = Math.min(Math.max(v.y, H / 2 - height * k), H / 2);
      return { x, y, k };
    },
    [width, height],
  );

  const fit = useCallback(() => {
    const { W, H } = size();
    const k = Math.min(W / width, H / height);
    fitK.current = k;
    userMoved.current = false;
    setView({ k, x: (W - width * k) / 2, y: (H - height * k) / 2 });
  }, [width, height]);

  useLayoutEffect(() => {
    fit();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const { W, H } = size();
      fitK.current = Math.min(W / width, H / height);
      if (!userMoved.current) fit();
      else setView((v) => clamp(v));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit, clamp, width, height]);

  const zoomAt = useCallback(
    (factor: number, px?: number, py?: number) => {
      const { W, H } = size();
      const cx = px ?? W / 2;
      const cy = py ?? H / 2;
      userMoved.current = true;
      setView((v) => {
        const k = Math.min(Math.max(v.k * factor, fitK.current * 0.85), fitK.current * MAX_ZOOM);
        const f = k / v.k;
        return clamp({ k, x: cx - (cx - v.x) * f, y: cy - (cy - v.y) * f });
      });
    },
    [clamp],
  );

  // Roda do mouse (listener nativo para poder impedir a rolagem da página).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { left, top } = size();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomAt(Math.exp(-delta * 0.0015), e.clientX - left, e.clientY - top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  // Espaço segurado: modo arrastar. + / − / 0 pelo teclado.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (isTyping(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.code === 'Space') {
        const t = e.target as HTMLElement;
        if (t.tagName === 'BUTTON' || t.getAttribute('role') === 'button') return;
        e.preventDefault();
        setSpace(true);
      } else if (e.key === '+' || e.key === '=') zoomAt(1.25);
      else if (e.key === '-' || e.key === '_') zoomAt(0.8);
      else if (e.key === '0') fit();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpace(false);
    };
    const blur = () => setSpace(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [zoomAt, fit]);

  const viewRef = useRef(view);
  viewRef.current = view;

  function onPointerDown(e: React.PointerEvent) {
    const g = gesture.current;
    g.suppressClick = false;
    if (e.button !== 0 && e.button !== 1) return;
    if ((e.target as HTMLElement).closest('.map-overlay-ui')) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size === 2) {
      const [a, b] = [...g.pointers.values()];
      const { left, top } = size();
      g.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2 - left, my: (a.y + b.y) / 2 - top, view: viewRef.current };
      g.start = undefined;
      g.moved = true;
      return;
    }
    g.start = { x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y, id: e.pointerId };
    g.moved = false;
    if (space || e.button === 1) {
      e.preventDefault();
      g.moved = true;
      ref.current?.setPointerCapture(e.pointerId);
      setDragging(true);
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const g = gesture.current;
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pinch && g.pointers.size >= 2) {
      const [a, b] = [...g.pointers.values()];
      const { left, top } = size();
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mx = (a.x + b.x) / 2 - left;
      const my = (a.y + b.y) / 2 - top;
      const p = g.pinch;
      const k = Math.min(Math.max(p.view.k * (dist / p.dist), fitK.current * 0.85), fitK.current * MAX_ZOOM);
      const f = k / p.view.k;
      userMoved.current = true;
      setView(clamp({ k, x: mx - (p.mx - p.view.x) * f, y: my - (p.my - p.view.y) * f }));
      return;
    }
    const s = g.start;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!g.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      g.moved = true;
      ref.current?.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    if (g.moved) {
      userMoved.current = true;
      setView((v) => clamp({ k: v.k, x: s.vx + dx, y: s.vy + dy }));
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    const g = gesture.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size < 2) g.pinch = undefined;
    if (g.pointers.size === 0) {
      // O clique que o navegador gera ao soltar depois de arrastar não vale; o próximo pointerdown libera.
      if (g.moved) g.suppressClick = true;
      g.start = undefined;
      g.moved = false;
      setDragging(false);
    }
  }

  const project: Project = (x, y) => ({ left: view.x + x * view.k, top: view.y + y * view.k });
  const zoomPct = Math.round((view.k / fitK.current) * 100);

  return (
    <div
      ref={ref}
      className={`map-viewport${space ? ' grab' : ''}${dragging ? ' dragging' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={(e) => {
        if (gesture.current.suppressClick || space) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onAuxClick={(e) => e.preventDefault()}
    >
      <div
        className="map-stage"
        style={{ width, height, transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
      >
        {children}
      </div>
      {overlay?.(project, view)}
      <div className="zoom-controls map-overlay-ui" role="group" aria-label="Zoom do mapa">
        <button className="btn small" onClick={() => zoomAt(1.25)} aria-label="Aproximar" title="Aproximar (+)">
          <Icon name="zoomIn" />
        </button>
        <button className="btn small" onClick={() => zoomAt(0.8)} aria-label="Afastar" title="Afastar (−)">
          <Icon name="zoomOut" />
        </button>
        <button className="btn small" onClick={fit} aria-label="Enquadrar o mapa" title="Enquadrar (0)">
          <Icon name="fit" />
        </button>
        <span className="zoom-pct num" aria-live="polite">
          {zoomPct}%
        </span>
      </div>
    </div>
  );
}
